import type { ValidationIssues } from '~/models/common'
import {
  buildNutritionPlanRecord,
  type NutritionPlanInput,
  type NutritionPlanRecord,
  supersedeNutritionPlan,
} from '~/models/nutritionPlan'
import { toIsoTimestamp, unwrapDate } from '~/types/dates'
import type { ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'
import { withTransaction } from '../transactions'

/**
 * Nutrition plan sources and their effective periods (D-096, pending).
 *
 * **Nothing here is deletable and nothing is overwritten.** A change of targets
 * writes a new record and closes the previous one, and the two happen in one
 * transaction — see `recordNewTargets`.
 */

export async function listNutritionPlans(
  db: SolaDatabase,
  projectId: ProjectId,
): Promise<Result<readonly NutritionPlanRecord[], StorageError>> {
  try {
    const plans = await db.getAllFromIndex(STORE.nutritionPlans, 'projectId', projectId)
    return ok(byEffectiveFrom(plans))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The plan in force now.
 *
 * Currency is the **absence** of `effectiveTo`, which IndexedDB cannot index, so
 * this reads the project's plans and takes the open one. There is at most one:
 * `recordNewTargets` closes the previous record in the same transaction that
 * opens the next. The latest-by-date tie-break exists only so a database left
 * inconsistent by some future bug resolves to the newest rather than to whichever
 * record IndexedDB happened to return first.
 */
export async function getCurrentNutritionPlan(
  db: SolaDatabase,
  projectId: ProjectId,
): Promise<Result<NutritionPlanRecord | undefined, StorageError>> {
  const plans = await listNutritionPlans(db, projectId)
  if (!plans.ok) return plans

  const open = plans.value.filter((plan) => plan.effectiveTo === undefined)
  return ok(open[open.length - 1])
}

/**
 * The plan that was in force on a given day.
 *
 * A weekly review reads a past week, and reading it against today's targets would
 * judge it by a number that did not exist then — the retroactive rewriting D-046
 * forbids for a finalized opinion. A period is inclusive at both ends, matching
 * `supersedeNutritionPlan`.
 */
export async function getNutritionPlanOn(
  db: SolaDatabase,
  projectId: ProjectId,
  date: string,
): Promise<Result<NutritionPlanRecord | undefined, StorageError>> {
  const plans = await listNutritionPlans(db, projectId)
  if (!plans.ok) return plans

  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  const covering = plans.value.filter((plan) => {
    if (unwrapDate(plan.effectiveFrom) > date) return false
    return plan.effectiveTo === undefined || unwrapDate(plan.effectiveTo) >= date
  })
  return ok(covering[covering.length - 1])
}

/**
 * Record what the authority now recommends, closing the previous period.
 *
 * Atomic by necessity. Two sequential writes failing between them leave either
 * two open plans — so "the current targets" is ambiguous — or none, so a day's
 * check has nothing to be read against. Both records are built before the
 * transaction opens and both requests are issued before either is awaited, which
 * is the pattern `finalizeWeeklyReview` established.
 *
 * This is the "two-field task" the product requires: new calories, new protein,
 * effective from today. The previous target survives with its period.
 */
export async function recordNewTargets(
  db: SolaDatabase,
  input: NutritionPlanInput,
  now: Date,
): Promise<Result<NutritionPlanRecord, StorageError>> {
  const built = buildNutritionPlanRecord(input, toIsoTimestamp(now))
  if (!built.ok) return err(invalid(built.error))
  const next = built.value

  const current = await getCurrentNutritionPlan(db, next.projectId)
  if (!current.ok) return current

  let closed: NutritionPlanRecord | undefined
  if (current.value !== undefined) {
    const superseded = supersedeNutritionPlan(
      current.value,
      next.effectiveFrom,
      toIsoTimestamp(now),
    )
    if (!superseded.ok) return err(invalid(superseded.error))
    closed = superseded.value
  }

  const written = await withTransaction(db, [STORE.nutritionPlans], async (tx) => {
    const store = tx.objectStore(STORE.nutritionPlans)
    const requests = closed === undefined ? [store.put(next)] : [store.put(closed), store.put(next)]
    /* Issued together, awaited afterwards. When one fails the transaction aborts
       and rejects the other too; awaiting them in sequence would leave the
       sibling's rejection unhandled. */
    for (const request of requests) void Promise.resolve(request).catch(() => undefined)
    await Promise.all(requests)
    return next
  })

  if (!written.ok) return written
  notifyWritten(STORE.nutritionPlans)
  return ok(written.value)
}

function byEffectiveFrom(plans: readonly NutritionPlanRecord[]): readonly NutritionPlanRecord[] {
  return [...plans].sort((left, right) => {
    const byDate = unwrapDate(left.effectiveFrom).localeCompare(unwrapDate(right.effectiveFrom))
    /* Tie-break on id so two plans sharing a start date order deterministically
       rather than by whichever IndexedDB returned first. */
    return byDate !== 0 ? byDate : left.id.localeCompare(right.id)
  })
}

function invalid(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}
